/**
 * Cliente de la API de Membresías (US-2.4) — Admin Web.
 *
 * Endpoints del contrato (`openapi.yaml`, tag `packs-membresias`):
 * - GET    /api/v1/memberships
 * - POST   /api/v1/memberships
 * - GET    /api/v1/memberships/{membershipId}
 * - PATCH  /api/v1/memberships/{membershipId}
 * - DELETE /api/v1/memberships/{membershipId} (baja lógica idempotente)
 *
 * Los tipos se derivan del contrato en `@goelectric/shared-types`; la red usa el
 * cliente HTTP centralizado (`apiRequest`) y su normalización de errores (`ApiError`).
 */
import type {
  CreateMembershipBody,
  ListMembershipsQuery,
  Membership,
  MembershipsPage,
  UpdateMembershipBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (búsqueda, estado + offset/limit) tal como los define el contrato. */
export type MembershipListParams = ListMembershipsQuery;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: MembershipListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const membershipApi = {
  list(params: MembershipListParams = {}): Promise<MembershipsPage> {
    return apiRequest<MembershipsPage>(`/api/v1/memberships${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(membershipId: string): Promise<Membership> {
    return apiRequest<Membership>(
      `/api/v1/memberships/${encodeURIComponent(membershipId)}`,
    );
  },

  create(body: CreateMembershipBody): Promise<Membership> {
    return apiRequest<Membership>('/api/v1/memberships', { method: 'POST', body });
  },

  update(membershipId: string, body: UpdateMembershipBody): Promise<Membership> {
    return apiRequest<Membership>(
      `/api/v1/memberships/${encodeURIComponent(membershipId)}`,
      {
        method: 'PATCH',
        body,
      },
    );
  },

  remove(membershipId: string): Promise<void> {
    return apiRequest<void>(
      `/api/v1/memberships/${encodeURIComponent(membershipId)}`,
      {
        method: 'DELETE',
      },
    );
  },
};
